import { Controller } from "@hotwired/stimulus";
import {
  computePosition,
  shift,
  autoPlacement,
  offset,
  arrow,
  autoUpdate,
  detectOverflow,
} from "@floating-ui/dom";

export default class extends Controller {
  static values = {
    i18nContent: Object,
    group: String,
    savedState: Object,
  };

  static targets = ["backdrop", "spotlight"];
  static activeTourController = null;

  static isAnyTourActive() {
    return this.activeTourController !== null;
  }

  static setActiveTour(controller) {
    this.activeTourController = controller;
  }

  static clearActiveTour() {
    this.activeTourController = null;
  }

  connect() {
    if (this.hasGroupValue) {
      this.steps = this.loadContent();
      this.currentUserId = document.head.querySelector("meta[name=user_id]").content;
      this.currentStepIndex = 0;
      this.popover = null;
      this.cleanup = null;
      this.keyboardHandler = null;
      this.initTour();
    }
  }

  disconnect() {
    this.closeTour();
  }

  initTour() {
    if (this.steps.length === 0) {
      return;
    }

    if (this.wasTourPreviouslyCompleted()) {
      return;
    }

    if (this.constructor.isAnyTourActive()) {
      return;
    }

    const savedStep = this.savedStateValue["nu_current_step"];
    if (savedStep) {
      const stepIndex = parseInt(savedStep, 10);
      if (stepIndex >= 0 && stepIndex < this.steps.length) {
        this.currentStepIndex = stepIndex;
      }
    }
    this.launchTour();
  }

  launchBtnClick() {
    if (this.constructor.isAnyTourActive()) {
      this.constructor.activeTourController.closeTour();
    }

    this.currentStepIndex = 0;
    this.launchTour();
  }

  launchTour() {
    if (this.isTestEnvironment) {
      return;
    }

    if (!this.hasGroupValue) {
      return;
    }

    if (this.steps.length === 0) {
      return;
    }

    this.constructor.setActiveTour(this);
    this.showStep(this.currentStepIndex);
  }

  showStep(stepIndex) {
    if (stepIndex < 0 || stepIndex >= this.steps.length) {
      stepIndex = 0;
    }

    this.currentStepIndex = stepIndex;

    document.querySelector(".tourguide-highlight")?.classList.remove("tourguide-highlight");

    if (this.popover) {
      this.closeTour();
    }

    const step = this.steps[stepIndex];

    if (
      step.target &&
      step.target !== document.body &&
      this.isElementOutsideViewport(step.target)
    ) {
      this.scrollToElement(step.target);
    }

    if (step.target.closest(".modal")) {
      this.setupModal(step);
    } else {
      this.setupPopover(step);
    }
  }

  setupModal(step) {
    const modal = step.target.closest(".modal");
    if (!modal.classList.contains("show")) {
      modal.addEventListener(
        "shown.bs.modal",
        () => {
          document
            .querySelectorAll(
              ".modal-open .tourguide-backdrop, .modal-open .tourguide-spotlight, .modal-open .tourguide-popover",
            )
            .forEach((el) => {
              el.classList.add("tourguide-modal-visible");
            });
          this.setupPopover(step);
        },
        {
          once: true,
        },
      );
    }
    modal.addEventListener("hide.bs.modal", () => this.closeTour());
  }

  setupPopover(step) {
    this.createPopover(step);
    this.positionPopover(step.target);
    this.setupStepEventListeners();
  }

  setupStepEventListeners() {
    setTimeout(() => {
      if (this.popover) {
        this.popover.focus({ focusVisible: false });
      }
    }, 100);

    if (!this.resizeHandler) {
      this.resizeHandler = () => {
        if (this.steps[this.currentStepIndex]) {
          this.updateBackdropSpotlight(this.steps[this.currentStepIndex].target);
        }
      };
    }
    window.addEventListener("resize", this.resizeHandler);

    if (!this.keyboardHandler) {
      this.keyboardHandler = (event) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          this.prevStep();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          this.nextStep();
        } else if (event.key === "Escape" && this.wasTourPreviouslyCompleted()) {
          event.preventDefault();
          this.closeTour();
        }
      };
    }
    document.addEventListener("keydown", this.keyboardHandler);
  }

  createPopover(step) {
    this.popover = document.createElement("div");
    this.popover.className = "tourguide-popover";
    this.popover.setAttribute("role", "dialog");
    this.popover.setAttribute("aria-modal", "true");
    this.popover.setAttribute("aria-labelledby", "tourguide-title");
    this.popover.setAttribute("aria-describedby", "tourguide-body tourguide-instructions");
    this.popover.setAttribute("tabindex", "-1");

    if (step.target.closest(".modal")) {
      this.popover.classList.add("tourguide-modal-visible");
    }

    const stepNumber = this.currentStepIndex + 1;
    const totalSteps = this.steps.length;
    const isLastStep = this.currentStepIndex >= this.steps.length - 1;
    const isFirstStep = this.currentStepIndex === 0;
    const stepTitle = step.title;
    const stepContent = step.content;
    let buttonLabel = step.buttonLabel;

    if (buttonLabel) {
      buttonLabel = buttonLabel;
    } else if (isLastStep) {
      buttonLabel = "Finish";
    } else {
      buttonLabel = "Next";
    }

    this.popover.innerHTML = `
      <div class="tourguide-content p-3">
        <div class="tourguide-header d-flex justify-content-between align-items-start gap-3 mb-3">
          <h6 id="tourguide-title" class="tourguide-title m-0">${stepTitle}</h6>
          ${this.wasTourPreviouslyCompleted() ? '<button class="tourguide-close btn btn-close" type="button" aria-label="Close tour"></button>' : ""}
        </div>
        <div id="tourguide-body" class="tourguide-body mb-3">
          ${stepContent}
        </div>
        <div class="tourguide-footer d-flex justify-content-between align-items-center">
          <button class="btn btn-secondary ${isFirstStep ? "invisible" : ""} tourguide-prev" type="button"
                  aria-label="Go to previous step"
                  ${isFirstStep ? 'aria-disabled="true"' : ""}>Back</button>
          ${
            totalSteps > 1
              ? `<div class="tourguide-progress d-flex gap-1 align-items-center" role="progressbar" aria-label="Tour progress: step ${stepNumber} of ${totalSteps}">
              ${this.generateProgressDots()}
            </div>`
              : ""
          }
          <button class="btn btn-primary tourguide-next" type="button" aria-label="${buttonLabel}">${buttonLabel}</button>
        </div>
      </div>
      <div class="tourguide-arrow" aria-hidden="true"></div>
    `;

    const nextButton = this.popover.querySelector(".tourguide-next");
    const prevButton = this.popover.querySelector(".tourguide-prev");

    prevButton?.addEventListener("click", () => this.prevStep());
    nextButton?.addEventListener("click", () => this.nextStep());

    if (step.interactionElement) {
      this.setupInteraction(step);
    } else {
      this.interactionRequired = false;
    }

    if (this.wasTourPreviouslyCompleted()) {
      this.popover
        .querySelector(".tourguide-close")
        .addEventListener("click", () => this.closeTour());
      this.backdropTarget.addEventListener("click", () => this.closeTour());
    }

    this.backdropTarget.classList.remove("d-none");
    document.body.appendChild(this.popover);
  }

  setupInteraction(step) {
    this.interactionRequired = true;
    const interactionElement = step.interactionElement;
    const tagName = interactionElement.tagName.toLowerCase();
    const inputType = interactionElement.type?.toLowerCase();

    if (this.wasTourPreviouslyCompleted()) {
      this.interactionRequired = false;
    } else {
      this.hideTourButtons();
      this.disableModalButtons(step);
    }

    if (tagName === "form" || inputType === "submit") {
      const el = tagName === "form" ? interactionElement : interactionElement.closest("form");
      this.setupInteractionListener(step, el, "submit", this.nextStep.bind(this));
    } else if (
      tagName === "input" &&
      ["text", "email", "password", "number", "tel", "url", "search", "file"].includes(inputType)
    ) {
      this.setupInteractionListener(
        step,
        interactionElement,
        "input",
        this.enableTourButtons.bind(this),
      );
    } else if (tagName === "textarea") {
      this.setupInteractionListener(
        step,
        interactionElement,
        "input",
        this.enableTourButtons.bind(this),
      );
    } else if (tagName === "input" && ["checkbox", "radio"].includes(inputType)) {
      this.setupInteractionListener(
        step,
        interactionElement,
        "change",
        this.enableTourButtons.bind(this),
      );
    } else if (tagName === "select") {
      this.setupInteractionListener(
        step,
        interactionElement,
        "change",
        this.enableTourButtons.bind(this),
      );
    } else {
      this.setupInteractionListener(step, interactionElement, "click", this.nextStep.bind(this));
    }
  }

  setupInteractionListener(step, el, eventType, callback) {
    el.addEventListener(
      eventType,
      () => {
        this.completeInteraction(step);
        callback();
      },
      { once: true },
    );
  }

  disableModalButtons(step) {
    const modal = step.target.closest(".modal");
    if (!modal) {
      return;
    }

    modal.querySelectorAll('[data-bs-dismiss="modal"]').forEach((element) => {
      element.setAttribute("disabled", true);
    });
  }

  hideTourButtons() {
    const nextButton = this.popover.querySelector(".tourguide-next");

    nextButton.classList.add("invisible");
  }

  enableTourButtons() {
    const nextButton = this.popover.querySelector(".tourguide-next");

    nextButton.classList.remove("invisible");
  }

  completeInteraction(step) {
    step.interactionElement?.classList.remove("tourguide-highlight");
    this.interactionRequired = false;
    step.interactionElement = null;
  }

  positionPopover(targetElement) {
    if (!this.popover || !targetElement) return;

    if (targetElement === document.body) {
      this.popover.classList.add("body-popover");
      return;
    }

    const arrowElement = this.popover.querySelector(".tourguide-arrow");

    this.cleanup = autoUpdate(targetElement, this.popover, () => {
      computePosition(targetElement, this.popover, {
        middleware: [
          offset(16),
          autoPlacement(),
          shift({ padding: 20 }),
          {
            name: "detectOverflow",
            async fn(state) {
              const overflow = await detectOverflow(state, { padding: 20 });

              if (
                overflow.top > 0 ||
                overflow.bottom > 0 ||
                overflow.left > 0 ||
                overflow.right > 0
              ) {
                const adjustedX = Math.max(
                  20,
                  Math.min(state.x, window.innerWidth - state.rects.floating.width - 20),
                );
                const adjustedY = Math.max(
                  20,
                  Math.min(state.y, window.innerHeight - state.rects.floating.height - 20),
                );

                return {
                  x: adjustedX,
                  y: adjustedY,
                };
              }

              return {};
            },
          },
          arrow({ element: arrowElement }),
        ],
      }).then(({ x, y, placement, middlewareData }) => {
        Object.assign(this.popover.style, {
          left: `${x}px`,
          top: `${y}px`,
        });

        if (middlewareData.arrow) {
          const { x: arrowX, y: arrowY } = middlewareData.arrow;
          const staticSide = {
            top: "bottom",
            right: "left",
            bottom: "top",
            left: "right",
          }[placement.split("-")[0]];

          Object.assign(arrowElement.style, {
            left: arrowX != null ? `${arrowX}px` : "",
            top: arrowY != null ? `${arrowY}px` : "",
            right: "",
            bottom: "",
            [staticSide]: "-4px",
          });
        }

        this.updateBackdropSpotlight(targetElement);
      });
    });
  }

  updateBackdropSpotlight(targetElement) {
    if (!this.backdropTarget || !targetElement) return;

    if (targetElement === document.body) {
      this.backdropTarget.classList.add("tourguide-backdrop--body-target");
      this.spotlightTarget.classList.add("d-none");
      return;
    }

    this.backdropTarget.classList.remove("tourguide-backdrop--body-target");

    const currentStep = this.steps[this.currentStepIndex];
    const allowInteraction = currentStep && currentStep.interactionElement;

    const rect = targetElement.getBoundingClientRect();
    const padding = 8;
    const borderRadius = 4;

    const left = Math.max(0, rect.left - padding);
    const top = Math.max(0, rect.top - padding);
    const width = Math.min(window.innerWidth, rect.right + padding) - left;
    const height = Math.min(window.innerHeight, rect.bottom + padding) - top;

    const cutoutX = left;
    const cutoutY = top;
    const cutoutWidth = width;
    const cutoutHeight = height;

    this.backdropTarget.style.clipPath = `polygon(
      0% 0%, 
      0% 100%, 
      ${cutoutX}px 100%, 
      ${cutoutX}px ${cutoutY}px, 
      ${cutoutX + cutoutWidth}px ${cutoutY}px, 
      ${cutoutX + cutoutWidth}px ${cutoutY + cutoutHeight}px, 
      ${cutoutX}px ${cutoutY + cutoutHeight}px, 
      ${cutoutX}px 100%, 
      100% 100%, 
      100% 0%
    )`;

    this.spotlightTarget.style.cssText = `
      left: ${left}px;
      top: ${top}px;
      width: ${width}px;
      height: ${height}px;
      border-radius: ${borderRadius}px;
      pointer-events: ${allowInteraction ? "none" : "auto"};
    `;

    this.spotlightTarget.classList.remove("d-none");
  }

  async nextStep() {
    if (this.interactionRequired) {
      return;
    }

    const nextIndex = this.currentStepIndex + 1;
    this.recordStatusUpdate();
    if (this.steps[this.currentStepIndex].redirect) {
      this.finishTour();
      this.handleRedirect(this.steps[this.currentStepIndex].redirect);
    } else if (nextIndex >= this.steps.length) {
      this.finishTour();
    } else {
      this.showStep(nextIndex);
    }
  }

  prevStep() {
    this.showStep(this.currentStepIndex - 1);
  }

  async finishTour() {
    this.closeTour();
  }

  closeTour() {
    if (this.constructor.activeTourController === this) {
      this.constructor.clearActiveTour();
    }

    if (this.cleanup) {
      this.cleanup();
      this.cleanup = null;
    }

    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
      this.resizeHandler = null;
    }

    if (this.keyboardHandler) {
      document.removeEventListener("keydown", this.keyboardHandler);
      this.keyboardHandler = null;
    }

    if (this.popover) {
      this.popover.remove();
      this.popover = null;
    }

    document.querySelector(".tourguide-highlight")?.classList.remove("tourguide-highlight");

    this.backdropTarget.style.clipPath = "none";
    this.backdropTarget.classList.add("d-none");
    this.backdropTarget.classList.remove("tourguide-backdrop--body-target");
    this.spotlightTarget.classList.add("d-none");
  }

  loadContent() {
    try {
      const i18nContent = this.i18nContentValue;
      return Object.keys(this.i18nContentValue)
        .map(function (i) {
          const entry = i18nContent[i];
          const target = document.querySelector(entry.target);
          const title = entry.title || null;
          const content = entry.content || null;
          const interactionElement = entry.interaction_target
            ? document.querySelector(entry.interaction_target)
            : null;

          if (entry.interaction_target && !interactionElement) {
            return;
          }

          if (
            target &&
            content &&
            typeof content == "string" &&
            title &&
            typeof title == "string"
          ) {
            return {
              title: title,
              content: content,
              target: target,
              position: entry.position || "top",
              redirect: entry.redirect || null,
              buttonLabel: entry.button_label,
              interactionElement: interactionElement,
            };
          }
        })
        .filter(function (step) {
          return step !== undefined;
        });
    } catch (error) {
      console.error("Error loading i18n content:", error);
      return [];
    }
  }

  handleRedirect(redirectPath) {
    if (typeof Turbo !== "undefined" && Turbo.visit) {
      Turbo.visit(redirectPath);
    } else {
      window.location.href = redirectPath;
    }
  }

  wasTourPreviouslyCompleted() {
    return !!this.savedStateValue["nu_completed_at"];
  }

  recordStatusUpdate() {
    if (this.wasTourPreviouslyCompleted()) {
      return Promise.resolve();
    }

    const action = this.currentStepIndex >= this.steps.length - 1 ? "complete" : "progress";

    return fetch(`/tours/${action}`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content,
      },
      body: JSON.stringify({
        nu_tour_status: {
          nu_tour_name: this.groupValue,
          nu_current_step: this.currentStepIndex + 1,
        },
      }),
    }).then((response) => {
      return response.json().then((state) => {
        this.savedStateValue = state;
        return state;
      });
    });
  }

  generateProgressDots() {
    return this.steps
      .map((_, index) => {
        const isActive = index === this.currentStepIndex;
        const className = isActive ? "active" : "";
        const stepNum = index + 1;
        return `<span class="tourguide-dot rounded ${className}"
                      role="presentation"
                      aria-label="Step ${stepNum}${isActive ? " (current)" : ""}"
                      title="Step ${stepNum}${isActive ? " (current)" : ""}"></span>`;
      })
      .join("");
  }

  isElementOutsideViewport(element) {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;

    const visibleTop = Math.max(0, rect.top);
    const visibleBottom = Math.min(windowHeight, rect.bottom);
    const visibleLeft = Math.max(0, rect.left);
    const visibleRight = Math.min(windowWidth, rect.right);

    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
    const visibleWidth = Math.max(0, visibleRight - visibleLeft);
    const visibleArea = visibleHeight * visibleWidth;

    const totalArea = rect.height * rect.width;

    return visibleArea < totalArea * 0.5;
  }

  scrollToElement(element) {
    const rect = element.getBoundingClientRect();
    const elementTop = rect.top + window.pageYOffset;
    const elementHeight = rect.height;
    const windowHeight = window.innerHeight;
    const popoverBuffer = 240;
    const availableHeight = windowHeight - popoverBuffer * 2;
    let scrollTo = elementTop - availableHeight / 2 + elementHeight / 2 - popoverBuffer;

    scrollTo = Math.max(0, scrollTo);

    const maxScroll = document.documentElement.scrollHeight - windowHeight;
    scrollTo = Math.min(scrollTo, maxScroll);

    window.scrollTo({
      top: scrollTo,
      behavior: "smooth",
    });
  }

  get isTestEnvironment() {
    return document.head.querySelector("meta[name=rails_env]").content === "test";
  }
}
